import countries from "utils/Data/countries.json";
import countryStateAirport from "utils/Data/countryStateAirport.json";

type AgeOption = { value: string; label: string };
type DaysOption = { value: string; label: string };

const generateAgeArray = (() => {
  let cachedMin: number | null = null;
  let cachedMax: number | null = null;
  let cachedArray: AgeOption[] | null = null;

  return (min: number, max: number): AgeOption[] => {
    if (min === cachedMin && max === cachedMax && cachedArray) {
      return cachedArray; // Return cached array if parameters haven't changed
    }

    // Generate new array if parameters have changed
    const ageArray: AgeOption[] = [];
    for (let i = min; i <= max; i++) {
      ageArray.push({ value: i.toString(), label: i.toString() });
    }

    // Cache the results
    cachedMin = min;
    cachedMax = max;
    cachedArray = ageArray;

    return ageArray;
  };
})();

const TRIPAGEOPTION: AgeOption[] = generateAgeArray(1, 90);

const TRIPDAYSOPTION: DaysOption[] = generateAgeArray(0, 30);

const GROUP_TYPE = [
  { value: "Mix", label: "Mix" },
  { value: "Family", label: "Family" },
  { value: "Couple", label: "Couple" },
  { value: "Ladies", label: "Ladies" },
  { value: "Men", label: "Men" },
];

const TRIP_TYPE = [
  { value: "Mountain", label: "Mountain" },
  { value: "Beach", label: "Beach" },
  { value: "City", label: "City" },
  { value: "Historical", label: "Historical" },
  { value: "Exploratory", label: "Exploratory" },
  { value: "Village", label: "Village" },
  { value: "Safari", label: "Safari" },
  { value: "Astronomy", label: "Astronomy" },
  { value: "Sunrise", label: "Sunrise" },
  { label: 'Wildlife', value: 'Wildlife' },
  { label: 'Spiritual', value: 'Spiritual' }
];

// Domestic (default) vs International trip tag.
// Rule enforced in the form: domestic <=> country "India".
const TRIP_SCOPE = [
  { value: "domestic", label: "Domestic (India)" },
  { value: "international", label: "International" },
];
const DOMESTIC = "domestic";
const INTERNATIONAL = "international";
const DEFAULT_DOMESTIC_COUNTRY = "India";

// Countries offered as the international DESTINATION (visiting country).
// Loaded from the shared country list JSON; India is excluded (India = domestic).
// Country-only selection: no state/district data is maintained for these.
const COUNTRY_OPTIONS = (countries as string[])
  .filter((c) => c !== DEFAULT_DOMESTIC_COUNTRY)
  .map((c) => ({ value: c, label: c }));

// country -> state/region -> [international airports]. Powers the international
// start/end pickers (country -> state -> auto-selected airport). Includes India,
// since an international trip usually departs from / returns to India.
type CountryStateAirport = Record<string, Record<string, string[]>>;
const AIRPORT_DATA = countryStateAirport as CountryStateAirport;

// Countries that have international-airport data (start/end country dropdown).
const AIRPORT_COUNTRY_OPTIONS = Object.keys(AIRPORT_DATA).map((c) => ({
  value: c,
  label: c,
}));

// States/regions available for a given international start/end country.
const getStatesForCountry = (country: string) =>
  Object.keys(AIRPORT_DATA[country] || {}).map((s) => ({ value: s, label: s }));

// Primary (auto-selected) international airport for a country + state.
const getPrimaryAirport = (country: string, state: string): string =>
  (AIRPORT_DATA[country]?.[state] || [])[0] || "";

export {
  TRIPAGEOPTION,
  GROUP_TYPE,
  TRIP_TYPE,
  TRIPDAYSOPTION,
  TRIP_SCOPE,
  COUNTRY_OPTIONS,
  DOMESTIC,
  INTERNATIONAL,
  DEFAULT_DOMESTIC_COUNTRY,
  AIRPORT_COUNTRY_OPTIONS,
  getStatesForCountry,
  getPrimaryAirport,
};
